import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { login as loginApi, register as registerApi, logout as logoutApi, checkAccountStatus as checkAccountStatusApi } from '../services/authService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accountStatus, setAccountStatus] = useState({ active: true });
  const [showStatusNotification, setShowStatusNotification] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const user = localStorage.getItem('user');
    if (user) {
      setCurrentUser(JSON.parse(user));
    }
    setLoading(false);
  }, []);

  const checkAccountStatus = useCallback(async () => {
    if (!currentUser) return { active: true };
    
    try {
      const status = await checkAccountStatusApi();
      setAccountStatus(status);
      setShowStatusNotification(!status.active);
      return status;
    } catch (error) {
      return { active: true };
    }
  }, [currentUser]);

  const login = async (email, password) => {
    try {
      const response = await loginApi({ email, password });
      const user = {
        email: response.email,
        token: response.access_token,
        role: response.role
      };
      localStorage.setItem('user', JSON.stringify(user));
      setCurrentUser(user);
      // Check account status after login
      await checkAccountStatus();
      return { success: true };
    } catch (error) {
      return { success: false, message: error.response?.data?.message || error.message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await registerApi(userData);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const logout = async () => {
    try {
      await logoutApi();
      localStorage.removeItem('user');
      setCurrentUser(null);
        window.location.reload();
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  // Check account status periodically
  useEffect(() => {
    if (currentUser) {
      // Initial check
      checkAccountStatus();
      
      // Check every 5 minutes
      const interval = setInterval(checkAccountStatus, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [currentUser, checkAccountStatus]);

  const value = {
    currentUser,
    accountStatus,
    showStatusNotification,
    setShowStatusNotification,
    login,
    register,
    logout,
    checkAccountStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
